import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileText, Loader2, HelpCircle } from "lucide-react";
import api from "@/utils/api";
import { toast } from "@/hooks/use-toast";
import {
  setCfcData as CfcData,
  setCurrentTab as CfcCurrentTab,
  setUserInitiatedNav as CfcUserInitiatedNav,
} from "../../../store/slices/cfcSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import FilePreview from "../../../components/common/FilePreview";
import { MultiSelect } from "../../../components/ui/multiselect";
import { Separator } from "@/components/ui/separator";

const particularsSchema = z
  .object({
    is_tie_up_made: z.string().min(1, "This field is required"),
    tie_up_attachment: z.any().optional(),
    turnover: z.string().min(1, "Annual Turnover is required"),
    new_entrepreneur: z.string().min(1, "This field is required"),
    new_spv: z.string().optional(),
    willingness_to_switch: z
      .string()
      .min(1, "Willingness to Switch is required"),
    willingness_description: z.string().optional(),
    capital_investment: z.string().min(1, "Capital Investment is required"),
    revenue: z.string().min(1, "Revenue is required"),
    total_investment: z.string().min(1, "Total Investment is required"),
    machinery_cost: z.string().min(1, "Machinery Cost is required"),
    infrastructure_cost: z.string().min(1, "Infrastructure Cost is required"),
    equipment_cost: z.string().min(1, "Equipment Cost is required"),
    shed_cost: z.string().min(1, "Shed Cost is required"),
    other_project_cost_type: z.string().optional(),
    other_project_cost: z.string().optional(),
    total_project_cost: z.string().min(1, "Total Project Cost is required"),
    source_of_income: z
      .array(z.string())
      .min(1, "At least one Source of Income is required"),
    own_fund_percent: z.string().optional(),
    bank_loan_percent: z.string().optional(),
    private_finance_percent: z.string().optional(),
    other_percent_type: z.string().optional(),
    other_percent: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const own = Number(data.own_fund_percent || 0);
    const bank = Number(data.bank_loan_percent || 0);
    const privateFinance = Number(data.private_finance_percent || 0);
    const other = Number(data.other_percent || 0);

    const total = own + bank + privateFinance + other;

    if (total > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Total percentage cannot exceed 100%",
        path: ["source_of_income"],
      });
    }
    if (data.is_tie_up_made === "Yes") {
      const hasFile = data.tie_up_attachment instanceof File;
      const hasUrl =
        typeof data.tie_up_attachment === "string" &&
        data.tie_up_attachment.length > 0;

      if (!hasFile && !hasUrl) {
        ctx.addIssue({
          path: ["tie_up_attachment"],
          code: z.ZodIssueCode.custom,
          message: "Marketing tieup details document is required",
        });
      }
    }
    if (data.source_of_income.includes("Own Fund")) {
      if (!data?.own_fund_percent) {
        ctx.addIssue({
          path: ["own_fund_percent"],
          code: z.ZodIssueCode.custom,
          message: "Own fund percent is required",
        });
      }
    }
    if (data.source_of_income.includes("Bank Loan")) {
      if (!data?.bank_loan_percent) {
        ctx.addIssue({
          path: ["bank_loan_percent"],
          code: z.ZodIssueCode.custom,
          message: "Bank loan percent required",
        });
      }
    }
    if (data.source_of_income.includes("Private Finance")) {
      if (!data?.private_finance_percent) {
        ctx.addIssue({
          path: ["private_finance_percent"],
          code: z.ZodIssueCode.custom,
          message: "Private Finance percent is required",
        });
      }
    }
    if (data.source_of_income.includes("Any other (specify)")) {
      if (!data?.other_percent_type) {
        ctx.addIssue({
          path: ["other_percent_type"],
          code: z.ZodIssueCode.custom,
          message: "Other Contribution Percent Type is required",
        });
      }
      if (!data.other_percent) {
        ctx.addIssue({
          path: ["other_percent"],
          code: z.ZodIssueCode.custom,
          message: "Other Contribution percent is required",
        });
      }
    }
    if (data?.other_project_cost && !data?.other_project_cost_type) {
      ctx.addIssue({
        path: ["other_project_cost_type"],
        code: z.ZodIssueCode.custom,
        message: "Other project cost type is required",
      });
    }
    if (data.other_project_cost_type && !data?.other_project_cost) {
      ctx.addIssue({
        path: ["other_project_cost"],
        code: z.ZodIssueCode.custom,
        message: "Other project cost  is required",
      });
    }

    if (data.willingness_to_switch === "yes") {
      if (!data?.willingness_description) {
        ctx.addIssue({
          path: ["willingness_description"],
          code: z.ZodIssueCode.custom,
          message: "Description is required",
        });
      }
    }

    if (data.new_entrepreneur === "Yes" && !data.new_spv) {
      ctx.addIssue({
        path: ["new_spv"],
        code: z.ZodIssueCode.custom,
        message: "Number of new entrepreneurs is required",
      });
    }
  });

const PriorityDetails = ({ data }) => {
  const [filePreviews, setFilePreviews] = useState({
    tie_up_attachment: null,
  });
  const location = useLocation();
  const nav = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buttonType, setButtonType] = useState("submit");
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    setFocus,
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: zodResolver(particularsSchema),
    defaultValues: {
      is_tie_up_made: data?.additional_detail?.is_tie_up_made ?? "",
      turnover: data?.additional_detail?.turnover ?? "",
      new_entrepreneur: data?.additional_detail?.new_entrepreneur ?? "",
      new_spv: data?.additional_detail?.new_spv ?? "",
      cluster_approach: data?.additional_detail?.cluster_approach ?? "",
      willingness_to_switch: data?.additional_detail?.willingness_to_switch ?? "",
      willingness_description: data?.additional_detail?.willingness_description ?? "",
      capital_investment: data?.additional_detail?.capital_investment ?? "",
      revenue: data?.additional_detail?.revenue ?? "",
      total_investment: data?.additional_detail?.total_investment ?? "",
      machinery_cost: data?.additional_detail?.machinery_cost ?? "",
      infrastructure_cost: data?.additional_detail?.infrastructure_cost ?? "",
      equipment_cost: data?.additional_detail?.equipment_cost ?? "",
      shed_cost: data?.additional_detail?.shed_cost ?? "",
      other_project_cost_type: data?.additional_detail?.other_project_cost_type ?? "",
      other_project_cost: data?.additional_detail?.other_project_cost ?? "",
      total_project_cost: data?.additional_detail?.total_project_cost ?? "",
      source_of_income: data?.additional_detail?.source_of_income ?? "",
      own_fund_percent: data?.additional_detail?.own_fund_percent ?? "",
      bank_loan_percent: data?.additional_detail?.bank_loan_percent ?? "",
      private_finance_percent: data?.additional_detail?.private_finance_percent ?? "",
      other_percent_type: data?.additional_detail?.other_percent_type ?? "",
      other_percent: data?.additional_detail?.other_percent ?? "",
      tie_up_attachment: data?.additional_detail?.tie_up_attachment ?? "",
    },
  });

  const formData = watch();

  useEffect(() => {
    if (data?.additional_detail) {
      setFilePreviews({
        tie_up_attachment: data?.additional_detail?.tie_up_attachment ?? "",
      });
    }
  }, [data]);

  useEffect(() => {
    const capital = Number(formData.capital_investment || 0);
    const revenue = Number(formData.revenue || 0);

    const totalInvestment = capital + revenue;
    handleInputChange("total_investment", totalInvestment.toString());
  }, [formData.capital_investment, formData.revenue]);

  useEffect(() => {
    const m = Number(formData.machinery_cost || 0);
    const infra = Number(formData.infrastructure_cost || 0);
    const eq = Number(formData.equipment_cost || 0);
    const shed = Number(formData.shed_cost || 0);
    const other = Number(formData.other_project_cost || 0);

    const totalCost = m + infra + eq + shed + other;
    handleInputChange("total_project_cost", totalCost.toString());
  }, [
    formData.machinery_cost,
    formData.infrastructure_cost,
    formData.equipment_cost,
    formData.shed_cost,
    formData.other_project_cost,
  ]);

  const handleFileUpload = async (name, file) => {
    if (file) {
      setValue(name, file, { shouldValidate: true });

      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreviews((prev) => ({ ...prev, [name]: e.target.result }));
        };
        reader.readAsDataURL(file);
      }

      await trigger(name);
    }
  };

  const handleRemoveFile = (fieldName) => {
    setValue(fieldName, undefined, { shouldValidate: true });
    setFilePreviews((prev) => ({ ...prev, [fieldName]: null }));
  };

  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      const searchParams = new URLSearchParams(location?.search);
      const submitData = new FormData();

      Object.keys(formData).forEach((key) => {
        if (formData[key] !== undefined && formData[key] !== null) {
          if (formData[key] instanceof File) {
            submitData.append(key, formData[key]);
          } else if (Array.isArray(formData[key])) {
            submitData.append(key, JSON.stringify(formData[key]));
          } else {
            submitData.append(key, formData[key]);
          }
        }
      });

      submitData.append("id", data?.id || "");
      let response;

      response = await api.post("/cfc-subsidies/priority_details", submitData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      dispatch(CfcData(response?.data?.data || {}));
      dispatch(CfcUserInitiatedNav(true));
      if (buttonType === "next") {
        dispatch(CfcCurrentTab("declaration"));
      }

      toast({
        title: "Success!",
        description: "Application Saved successfully",
        variant: "success",
      });
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Submission Failed",
        description:
          error.response?.data?.message || "Failed to submit application",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = async (fieldName, value) => {
    setValue(fieldName, value, { shouldValidate: true });
  };

  const onSubmitError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (firstErrorField) {
      setFocus(firstErrorField);
    }
  };

  const sourceOfIncomeSelected = (type) => {
    return (
      Array.isArray(formData.source_of_income) &&
      formData.source_of_income.includes(type)
    );
  };

  const onSubmitClick = async (e, type) => {
    e.preventDefault();
    setButtonType(type); // Save button type first
    console.log("🖱️ Submit button clicked. Type:", type);

    const isValid = await trigger();
    console.log("✅ Validation result:", isValid);
    console.log("⚠️ Validation errors:", errors);

    if (isValid) {
      console.log("🎯 Form is valid, calling handleSubmitSpv...");
    } else {
      toast({
        title: "Validation Error",
        description: "Please fix the errors before submitting",
        variant: "destructive",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onSubmitError)} noValidate>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Additional Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 mt-5">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="is_tie_up_made">
                Whether you have made any tie-up arrangement with manufacturer /
                running an own industry <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.is_tie_up_made || ""}
                onValueChange={(value) => {
                  handleInputChange("is_tie_up_made", value);
                  if (value === "No") {
                    handleInputChange("tie_up_types", []);
                  }
                }}
              >
                <SelectTrigger
                  className={errors.is_tie_up_made ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select Yes or No" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
              {errors.is_tie_up_made && (
                <p className="text-sm text-red-500">
                  {errors.is_tie_up_made.message}
                </p>
              )}
            </div>

            {formData.is_tie_up_made === "Yes" && (
              <div className="space-y-2">
                <Label
                  htmlFor="tie_up_attachment"
                  className="flex items-center gap-4"
                  required
                >
                  <span>Upload Document </span>
                  <Popover>
                    <PopoverTrigger>
                      <HelpCircle className="text-primary" />
                    </PopoverTrigger>
                    <PopoverContent>
                      <ol className="list-disc pl-4 space-y-2 text-sm">
                        <li>Details of marketing tie up arrangements</li>
                        <li>Details of own manufacturing industries</li>
                        <li>Sources of raw materials arrangements</li>
                        <li>Number of labour employed</li>
                        <li>Value added varieties produced</li>
                        <li>Details of turnover</li>
                        <li>Details of New Entrepreneurs</li>
                        <li>
                          Details of Export oriented Production (if Applicable)
                        </li>
                      </ol>
                    </PopoverContent>
                  </Popover>
                </Label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center">
                  <Input
                    id="tie_up_attachment"
                    type="file"
                    accept=".jpg,.png,.jpeg,.pdf"
                    onChange={(e) =>
                      handleFileUpload("tie_up_attachment", e.target.files[0])
                    }
                    className={errors.tie_up_attachment ? "border-red-500" : ""}
                  />

                  <FilePreview
                    fieldName="tie_up_attachment"
                    preview={filePreviews.tie_up_attachment}
                    file={
                      formData.tie_up_attachment instanceof File
                        ? formData.tie_up_attachment
                        : null
                    }
                    existingFile={
                      typeof filePreviews.tie_up_attachment === "string"
                        ? filePreviews.tie_up_attachment
                        : null
                    }
                    onRemove={handleRemoveFile}
                  />

                  {errors.tie_up_attachment && (
                    <p className="text-sm text-red-500">
                      {errors.tie_up_attachment.message}
                    </p>
                  )}

                  <p className="ms-1 text-[12px] text-muted-foreground mt-2">
                    Supported: JPG, JPEG, PNG, PDF (max 10MB)
                  </p>
                </div>
              </div>
            )}
          </div>

          <Separator />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="turnover">
                Average Business Turnover (in Rs.)
                <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  id="turnover"
                  value={formData.turnover || ""}
                  onChange={(e) => {
                    const value = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10);
                    handleInputChange("turnover", value);
                  }}
                  placeholder="Enter annual turnover in Rs."
                  className={errors.turnover ? "border-red-500" : ""}
                />
              </div>
              {errors.turnover && (
                <p className="text-sm text-red-500">
                  {errors.turnover.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="new_entrepreneur" required>
                Does the SPV include new entrepreneurs?
              </Label>

              <Select
                value={formData.new_entrepreneur || ""}
                onValueChange={(value) =>
                  handleInputChange("new_entrepreneur", value)
                }
              >
                <SelectTrigger
                  className={errors.new_entrepreneur ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select yes or no" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>

              {errors.new_entrepreneur && (
                <p className="text-sm text-red-500">
                  {errors.new_entrepreneur.message}
                </p>
              )}
            </div>

            {/* Conditional field shown only if 'Yes' is selected */}
            {formData.new_entrepreneur === "Yes" && (
              <div className="space-y-2">
                <Label htmlFor="new_spv">
                  Number of new entrepreneurs engaged in the SPV
                  <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="text"
                  id="new_spv"
                  value={formData.new_spv || ""}
                  onChange={(e) => handleInputChange("new_spv", e.target.value)}
                  className={errors.new_spv ? "border-red-500" : ""}
                />
                {errors.new_spv && (
                  <p className="text-sm text-red-500">
                    {errors.new_spv.message}
                  </p>
                )}
              </div>
            )}
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="willingness_to_switch" required>
                Willingness to Switch Over to Commercial Production and
                Innovations
              </Label>
              <Select
                value={formData.willingness_to_switch || ""}
                onValueChange={(value) =>
                  handleInputChange("willingness_to_switch", value)
                }
              >
                <SelectTrigger
                  className={
                    errors.willingness_to_switch ? "border-red-500" : ""
                  }
                >
                  <SelectValue placeholder="Select Yes or No" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
              {errors.willingness_to_switch && (
                <p className="text-sm text-red-500">
                  {errors.willingness_to_switch.message}
                </p>
              )}
            </div>

            {formData.willingness_to_switch === "yes" && (
              <div className="space-y-2">
                <Label htmlFor="willingness_description">
                  Enter Description <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="willingness_description"
                  {...register("willingness_description")}
                  rows={2}
                  className={
                    errors.willingness_description ? "border-red-500" : ""
                  }
                />
                {errors.willingness_description && (
                  <p className="text-sm text-red-500">
                    {errors.willingness_description.message}
                  </p>
                )}
              </div>
            )}
          </div>
          <Separator />

          <div>
            <Label className="my-2 font-bold">
              Details of Estimated Investment for the proposed CFC
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 mb-5 mt-2">
              <div className="space-y-2">
                <Label htmlFor="capital_investment" required>
                  Capital
                </Label>
                <Input
                  type="text"
                  id="capital_investment"
                  value={formData.capital_investment || ""}
                  onInput={(e) => {
                    const value = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 20);
                    handleInputChange("capital_investment", value);
                  }}
                  className={errors.capital_investment ? "border-red-500" : ""}
                />
                {errors.capital_investment && (
                  <p className="text-sm text-red-500">
                    {errors.capital_investment.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="revenue" required>
                  Revenue
                </Label>
                <Input
                  type="text"
                  id="revenue"
                  value={formData.revenue || ""}
                  onInput={(e) => {
                    const value = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 20);
                    handleInputChange("revenue", value);
                  }}
                  className={errors.revenue ? "border-red-500" : ""}
                />
                {errors.revenue && (
                  <p className="text-sm text-red-500">
                    {errors.revenue.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="total_investment" required>
                  Total
                </Label>
                <Input
                  type="text"
                  id="total_investment"
                  value={formData.total_investment || ""}
                  readOnly
                  onInput={(e) => {
                    const value = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 20);
                    handleInputChange("total_investment", value);
                  }}
                  className={errors.total_investment ? "border-red-500" : ""}
                />
                {errors.total_investment && (
                  <p className="text-sm text-red-500">
                    {errors.total_investment.message}
                  </p>
                )}
              </div>
            </div>
            <Label className="my-5 font-bold">
              Details of Estimated Project Cost for the proposed CFC
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 mb-5 mt-2">
              <div className="space-y-2">
                <Label htmlFor="machinery_cost" required>
                  Cost of Machinery
                </Label>
                <Input
                  type="text"
                  id="machinery_cost"
                  value={formData.machinery_cost || ""}
                  onInput={(e) => {
                    const value = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10);
                    handleInputChange("machinery_cost", value);
                  }}
                  className={errors.machinery_cost ? "border-red-500" : ""}
                />
                {errors.machinery_cost && (
                  <p className="text-sm text-red-500">
                    {errors.machinery_cost.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="infrastructure_cost" required>
                  Cost of Infrastructure
                </Label>
                <Input
                  type="text"
                  id="infrastructure_cost"
                  value={formData.infrastructure_cost || ""}
                  onInput={(e) => {
                    const value = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10);
                    handleInputChange("infrastructure_cost", value);
                  }}
                  className={errors.infrastructure_cost ? "border-red-500" : ""}
                />
                {errors.infrastructure_cost && (
                  <p className="text-sm text-red-500">
                    {errors.infrastructure_cost.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="equipment_cost" required>
                  Cost of Equipments
                </Label>
                <Input
                  type="text"
                  id="equipment_cost"
                  value={formData.equipment_cost || ""}
                  onInput={(e) => {
                    const value = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10);
                    handleInputChange("equipment_cost", value);
                  }}
                  className={errors.equipment_cost ? "border-red-500" : ""}
                />
                {errors.equipment_cost && (
                  <p className="text-sm text-red-500">
                    {errors.equipment_cost.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="shed_cost" required>
                  Cost of Shed
                </Label>
                <Input
                  type="text"
                  id="shed_cost"
                  value={formData.shed_cost || ""}
                  onInput={(e) => {
                    const value = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10);
                    handleInputChange("shed_cost", value);
                  }}
                  className={errors.shed_cost ? "border-red-500" : ""}
                />
                {errors.shed_cost && (
                  <p className="text-sm text-red-500">
                    {errors.shed_cost.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="other_project_cost">
                  Any Other Cost (Specify)
                </Label>
                <div className="flex gap-4">
                  <div className="w-full">
                    <Input
                      type="text"
                      id="other_project_cost_type"
                      value={formData.other_project_cost_type || ""}
                      placeholder="Enter Text"
                      onInput={(e) => {
                        const value = e.target.value;
                        handleInputChange("other_project_cost_type", value);
                      }}
                      className={
                        errors.other_project_cost_type ? "border-red-500" : ""
                      }
                    />
                    {errors.other_project_cost_type && (
                      <p className="text-sm text-red-500">
                        {errors.other_project_cost_type.message}
                      </p>
                    )}
                  </div>
                  <div className="w-full">
                    <Input
                      type="text"
                      id="other_project_cost"
                      placeholder="Enter Cost"
                      value={formData.other_project_cost || ""}
                      onInput={(e) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10);
                        handleInputChange("other_project_cost", value);
                      }}
                      className={
                        errors.other_project_cost ? "border-red-500" : ""
                      }
                    />
                    {errors.other_project_cost && (
                      <p className="text-sm text-red-500">
                        {errors.other_project_cost.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="total_project_cost" required>
                  Total Project Cost
                </Label>
                <Input
                  type="text"
                  id="total_project_cost"
                  value={formData.total_project_cost || ""}
                  readOnly
                  onInput={(e) => {
                    const value = e.target.value;
                    handleInputChange("total_project_cost", value);
                  }}
                  className={errors.total_project_cost ? "border-red-500" : ""}
                />
                {errors.total_project_cost && (
                  <p className="text-sm text-red-500">
                    {errors.total_project_cost.message}
                  </p>
                )}
              </div>
            </div>
            <Separator />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="source_of_income" required>
                  Source of Income
                </Label>
                <MultiSelect
                  options={[
                    {
                      id: "Own Fund",
                      name: "Own Fund",
                    },
                    {
                      id: "Bank Loan",
                      name: "Bank Loan",
                    },
                    {
                      id: "Private Finance",
                      name: "Private Finance",
                    },
                    {
                      id: "Any other (specify)",
                      name: "Any other (specify)",
                    },
                  ]}
                  value={formData.source_of_income || []}
                  onChange={(value) =>
                    handleInputChange("source_of_income", value)
                  }
                  placeholder="Select Tie-up Types"
                />
                {errors.source_of_income && (
                  <p className="text-sm text-red-500">
                    {errors.source_of_income.message}
                  </p>
                )}
              </div>
              {sourceOfIncomeSelected("Own Fund") && (
                <div className="space-y-2">
                  <Label htmlFor="own_fund_percent" required>
                    Contribution of Own Fund (%)
                  </Label>
                  <Input
                    type="text"
                    id="own_fund_percent"
                    value={formData.own_fund_percent || ""}
                    onInput={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 2);
                      handleInputChange("own_fund_percent", value);
                    }}
                    className={errors.own_fund_percent ? "border-red-500" : ""}
                  />
                  {errors.own_fund_percent && (
                    <p className="text-sm text-red-500">
                      {errors.own_fund_percent.message}
                    </p>
                  )}
                </div>
              )}
              {sourceOfIncomeSelected("Bank Loan") && (
                <div className="space-y-2">
                  <Label htmlFor="bank_loan_percent" required>
                    Contribution of Bank Loan (%)
                  </Label>
                  <Input
                    type="text"
                    id="bank_loan_percent"
                    value={formData.bank_loan_percent || ""}
                    onInput={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 2);
                      handleInputChange("bank_loan_percent", value);
                    }}
                    className={errors.bank_loan_percent ? "border-red-500" : ""}
                  />
                  {errors.bank_loan_percent && (
                    <p className="text-sm text-red-500">
                      {errors.bank_loan_percent.message}
                    </p>
                  )}
                </div>
              )}
              {sourceOfIncomeSelected("Private Finance") && (
                <div className="space-y-2">
                  <Label htmlFor="private_finance_percent" required>
                    Contribution of Private Finance (%)
                  </Label>
                  <Input
                    type="text"
                    id="private_finance_percent"
                    value={formData.private_finance_percent || ""}
                    onInput={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 2);
                      handleInputChange("private_finance_percent", value);
                    }}
                    className={
                      errors.private_finance_percent ? "border-red-500" : ""
                    }
                  />
                  {errors.private_finance_percent && (
                    <p className="text-sm text-red-500">
                      {errors.private_finance_percent.message}
                    </p>
                  )}
                </div>
              )}

              {sourceOfIncomeSelected("Any other (specify)") && (
                <div className="space-y-2">
                  <Label htmlFor="other_percent" required>
                    Other Contribution (%)
                  </Label>
                  <div className="flex gap-2">
                    <div className="w-full">
                      <Input
                        type="text"
                        id="other_percent_type"
                        value={formData.other_percent_type || ""}
                        onInput={(e) => {
                          const value = e.target.value;
                          handleInputChange("other_percent_type", value);
                        }}
                        placeholder="Enter other contribution type"
                        className={
                          errors.other_percent_type ? "border-red-500" : ""
                        }
                      />
                      {errors.other_percent_type && (
                        <p className="text-sm text-red-500">
                          {errors.other_percent_type.message}
                        </p>
                      )}
                    </div>
                    <div className="w-full">
                      <Input
                        type="text"
                        id="other_percent"
                        placeholder="Enter other contribution percent"
                        value={formData.other_percent || ""}
                        onInput={(e) => {
                          const value = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 2);
                          handleInputChange("other_percent", value);
                        }}
                        className={errors.other_percent ? "border-red-500" : ""}
                      />
                      {errors.other_percent && (
                        <p className="text-sm text-red-500">
                          {errors.other_percent.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end mt-6 gap-2">
            <Button
              type="submit"
              disabled={isSubmitting}
              onClick={() => setButtonType("submit")}
              // onClick={(e) => onSubmitClick(e, "submit")}
              className="min-w-[150px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
            <Button
              type="submit"
              onClick={() => setButtonType("next")}
              disabled={isSubmitting}
              className="min-w-[150px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  saving...
                </>
              ) : (
                "Save and Next"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};

export default PriorityDetails;
